/**
 * Utilidades para trabajar con geometría de medidas
 */

/**
 * Obtiene las coordenadas de una medida (soporta múltiples formatos)
 */
export const obtenerCoordenadasMedida = (cota) => {
  // Si tiene geometria_cota como objeto
  if (cota.geometria_cota && !Array.isArray(cota.geometria_cota)) {
    const geometria = cota.geometria_cota
    if (geometria.x_ini !== undefined && geometria.y_ini !== undefined && 
        geometria.x_fin !== undefined && geometria.y_fin !== undefined) {
      return {
        tipo: 'linea',
        x1: geometria.x_ini,
        y1: geometria.y_ini,
        x2: geometria.x_fin,
        y2: geometria.y_fin
      }
    }
  }
  
  // Si tiene geometria_cota como array
  if (cota.geometria_cota && Array.isArray(cota.geometria_cota) && cota.geometria_cota.length > 0) {
    const geometria = cota.geometria_cota[0]
    if (geometria && geometria.x_ini !== undefined && geometria.y_ini !== undefined && 
        geometria.x_fin !== undefined && geometria.y_fin !== undefined) {
      return {
        tipo: 'linea',
        x1: geometria.x_ini,
        y1: geometria.y_ini,
        x2: geometria.x_fin,
        y2: geometria.y_fin
      }
    }
  }
  
  // Mantener compatibilidad con coordenada (formato guardado)
  if (cota.coordenada) {
    const coords = cota.coordenada
    
    // Si es una línea
    if (coords.x_ini !== undefined && coords.y_ini !== undefined && 
        coords.x_fin !== undefined && coords.y_fin !== undefined) {
      return {
        tipo: 'linea',
        x1: coords.x_ini,
        y1: coords.y_ini,
        x2: coords.x_fin,
        y2: coords.y_fin
      }
    }
    
    // Si es un círculo
    if (coords.x !== undefined && coords.y !== undefined && coords.radio !== undefined) {
      return {
        tipo: 'circulo',
        x: coords.x,
        y: coords.y,
        radio: coords.radio
      }
    }
  }
  
  // Mantener compatibilidad con coordenadas_normalizadas
  if (cota.coordenadas_normalizadas) {
    const coords = cota.coordenadas_normalizadas
    
    // Si es una línea
    if (coords.x_ini !== undefined && coords.y_ini !== undefined && 
        coords.x_fin !== undefined && coords.y_fin !== undefined) {
      return {
        tipo: 'linea',
        x1: coords.x_ini,
        y1: coords.y_ini,
        x2: coords.x_fin,
        y2: coords.y_fin
      }
    }
    
    // Si es un círculo
    if (coords.x !== undefined && coords.y !== undefined && coords.radio !== undefined) {
      return {
        tipo: 'circulo',
        x: coords.x,
        y: coords.y,
        radio: coords.radio
      }
    }
  }
  
  return null
}

/**
 * Extrae la letra del label de una medida
 */
export const extraerLetraLabel = (cota) => {
  if (cota.label) {
    const match = cota.label.match(/^([A-Z])/)
    if (match) return match[1]
    return cota.label.charAt(0).toUpperCase()
  }
  
  if (cota.titulo) {
    const match = cota.titulo.match(/^([A-Z])/)
    if (match) return match[1]
    return cota.titulo.charAt(0).toUpperCase()
  }
  
  if (cota.referencia_manual?.etiqueta_estandar) {
    const match = cota.referencia_manual.etiqueta_estandar.match(/^([A-Z])/)
    if (match) return match[1]
    return cota.referencia_manual.etiqueta_estandar.charAt(0).toUpperCase()
  }
  
  return ''
}

/**
 * Calcula las coordenadas de la etiqueta para una medida
 * La etiqueta se posiciona en el punto medio de la línea o cerca del centro del círculo
 */
export const calcularCoordenadasEtiqueta = (cota) => {
  const coords = obtenerCoordenadasMedida(cota)
  
  if (!coords) return null
  
  if (coords.tipo === 'linea') {
    // Calcular el punto medio de la línea
    const x = (coords.x1 + coords.x2) / 2
    const y = (coords.y1 + coords.y2) / 2
    // Desplazar ligeramente hacia arriba y a la derecha para mejor visibilidad
    return {
      x: x + 20,
      y: y - 20
    }
  }
  
  if (coords.tipo === 'circulo') {
    // Posicionar la etiqueta cerca del centro del círculo, desplazada hacia arriba
    return {
      x: coords.x,
      y: coords.y - coords.radio - 20
    }
  }
  
  return null
}

/**
 * Obtiene todas las letras usadas en las medidas existentes
 */
export const obtenerLetrasUsadas = (coordenadas, medidasManuales) => {
  const letrasUsadas = new Set()
  const todasLasCoordenadas = [...coordenadas, ...medidasManuales]
  
  todasLasCoordenadas.forEach((cota) => {
    const letra = extraerLetraLabel(cota)
    if (letra && letra.length === 1 && /^[A-Z]$/.test(letra)) {
      letrasUsadas.add(letra)
    }
  })
  
  return letrasUsadas
}

/**
 * Calcula la siguiente letra disponible consecutivamente
 * Retorna la primera letra (A-Z) que no esté en uso
 */
export const calcularSiguienteLetraDisponible = (coordenadas, medidasManuales) => {
  const letrasUsadas = obtenerLetrasUsadas(coordenadas, medidasManuales)
  
  // Buscar la primera letra disponible desde A hasta Z
  for (let i = 0; i < 26; i++) {
    const letra = String.fromCharCode(65 + i) // A=65, B=66, ..., Z=90
    if (!letrasUsadas.has(letra)) {
      return letra
    }
  }
  
  // Si todas las letras están usadas, retornar null o la última
  return null
}
